<script setup lang="ts">
/**
 * Design System Button Component
 *
 * @example
 * <DSButton variant="primary" size="md">Click me</DSButton>
 * <DSButton variant="secondary" :loading="true">Loading...</DSButton>
 */

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  icon?: string
  iconPosition?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  disabled: false,
  fullWidth: false,
  iconPosition: 'left'
})

const buttonClasses = computed(() => {
  const classes = ['inline-flex', 'items-center', 'justify-center', 'gap-2', 'font-medium', 'rounded-md', 'transition-all', 'duration-200']

  // Size classes
  if (props.size === 'sm') {
    classes.push('py-2', 'px-3', 'text-sm')
  } else if (props.size === 'lg') {
    classes.push('py-3', 'px-6', 'text-lg')
  } else {
    classes.push('py-2.5', 'px-4', 'text-base')
  }

  // Variant classes
  if (props.variant === 'primary') {
    classes.push('bg-brand-600', 'text-white', 'hover:bg-brand-700', 'focus:ring-2', 'focus:ring-brand-500', 'focus:ring-offset-2')
  } else if (props.variant === 'secondary') {
    classes.push('bg-white', 'text-gray-700', 'border', 'border-gray-300', 'hover:bg-gray-50', 'focus:ring-2', 'focus:ring-brand-500', 'focus:ring-offset-2')
  } else if (props.variant === 'ghost') {
    classes.push('bg-transparent', 'text-gray-700', 'hover:bg-gray-100')
  } else if (props.variant === 'danger') {
    classes.push('bg-red-600', 'text-white', 'hover:bg-red-700', 'focus:ring-2', 'focus:ring-red-500', 'focus:ring-offset-2')
  }

  // Disabled state
  if (props.disabled || props.loading) {
    classes.push('opacity-50', 'cursor-not-allowed')
  }

  // Full width
  if (props.fullWidth) {
    classes.push('w-full')
  }

  return classes.join(' ')
})
</script>

<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    type="button"
  >
    <!-- Loading Spinner -->
    <UIcon
      v-if="loading"
      name="i-lucide-loader-2"
      class="w-4 h-4 animate-spin"
    />

    <!-- Left Icon -->
    <UIcon
      v-if="icon && iconPosition === 'left' && !loading"
      :name="icon"
      class="w-4 h-4"
    />

    <!-- Slot Content -->
    <slot />

    <!-- Right Icon -->
    <UIcon
      v-if="icon && iconPosition === 'right' && !loading"
      :name="icon"
      class="w-4 h-4"
    />
  </button>
</template>
